import React from 'react'
import styled, { css } from 'styled-components'
import moment from 'moment'
import { I18n } from 'react-redux-i18n'

const ItineraryDetails = styled.div`
  display: inline-block;
  width: 100%;
  padding: 0.5rem;
  background-color: #fff;
  border: 1px solid rgba(100, 100, 100, 0.1);
  border-radius: 6px;
  box-shadow: 0px 0px 10px -2px rgba(50, 50, 50, 0.1);
  transition: 0.3s;

  .type {
    display: block;
  }

  &:not(:last-child) {
    margin-bottom: 10px;
  }

  .details-row {
    display: flex;
    width: 100%;

    &:not(:last-child) {
      margin-bottom: 1em;
    }

    .separator {
      display: inline-block;
      height: 1.5em;
      width: 0px;
      border-left: 1px dashed rgba(50, 50, 50, 0.25);
      margin: 0 10px;
      position: relative;

      &:after {
        content: '';
        width: 6px;
        height: 6px;
        transform: rotate(45deg);
        border-right: 1px solid rgba(50, 50, 50, 0.25);
        border-bottom: 1px solid rgba(50, 50, 50, 0.25);
        position: absolute;
        bottom: -4px;
        left: -3px;
      }
    }
  }
`

const ItineraryPlace = styled.div`
  display: block;
  margin: 0 auto;

  &:not(:last-child) {
    margin-bottom: 10px;
  }

  .place,
  .flight-details {
    display: inline-block;
    vertical-align: top;
    font-size: 0.9em;
  }

  .place {
    text-align: left;
    font-weight: bold;

    ${(props) =>
  props.destination &&
      css`
        width: 100%;
        text-align: left;
      `}

    .date {
      font-size: 0.9em;
      color: rgba(0, 0, 0, 0.6);
      font-weight: normal;
    }
  }

  .flight-details {
    margin-left: 10px;
    padding-left: 10px;
    border-left: 1px solid rgba(100, 100, 100, 0.1);
  }

  .title {
    font-size: 1.35em;
    margin-bottom: 0.5em;
  }

  .address {
    color: rgba(50, 50, 50, 0.75);
    margin-bottom: 1em;
  }
`

const ItineraryFooter = styled.div`
  font-size: 0.9em;
  display: flex;
  flex-flow: row wrap;
  justify-content: space-between;
  align-items: center;
  background-color: rgba(185, 185, 185, 0.1);
  padding: 10px;
  border-radius: 6px;

  .rate {
    &:not(:last-child) {
      border-bottom: 1px solid rgba(215, 215, 215, 0.75);
      padding-bottom: 1em;
    }

    .room-type {
      margin-bottom: 2em;
    }

    .price {
      font-weight: bold;
      font-size: 1.25em;
      display: flex;
      justify-content: space-between;
    }

    .rate-comments {
      margin-top: 2em;

      div {
        font-size: 0.85em;

        &:not(:last-child) {
          margin-bottom: 1em;
        }
      }
    }
  }
`

const travelloopConnectors = [
  'api_welcomebeds_hotels',
  'api_restel_hotels',
  'api_hotusa_hotels',
  'api_keytel_hotels',
  'api_tour10_hotels',
  'api_travelloop_hotels',
  'api_goglobal_hotels',
  'api_abreuonline_hotels',
  'api_welcomebeds_hotels',
  'api_jumbobeds_hotels',
  'api_rhodasol_hotels',
  'api_totalstay_hotels',
  'api_w2m_hotels',
]

function getItineraryFragment({ type, data, provider }) {
  switch (type) {
    case 'flight': {
      const { flightNumber, duration, cabinClass, departure, arrival, stops, luggage, ticketNumber } = data
      return (
        <React.Fragment>
          <ItineraryPlace>
            <div className='place'>
              <div>
                {departure.airport.name ? departure.airport.name : ''} ({departure.airport.code})
              </div>
              <div className='date'>
                {departure.date} {departure.time}
              </div>
            </div>
            <div className='flight-details'>
              <div>
                <i className='fa fa-plane' /> {flightNumber}
              </div>
              {luggage
                ? luggage.quantity >= 0 && (
                  <div className='mr-2'>
                    <i className='fa fa-suitcase' /> {luggage.quantity} luggages
                  </div>
                )
                : 'no luggage'}
              <div>
                <span>Class: {cabinClass}</span>
              </div>
            </div>
          </ItineraryPlace>

          <div className='details-row'>
            <div className='separator'></div>
            <div>{stops && `${stops.length} stops - `}</div>
            <div>
              <div className='ml-1'>{`${Math.ceil(duration / 60) || 0}h`}</div>
            </div>
          </div>

          <ItineraryPlace destination>
            <div className='place'>
              <div>
                {arrival.airport.name} ({arrival.airport.code})
              </div>
              <div className='date'>
                {arrival.date} {arrival.time}
              </div>
            </div>
          </ItineraryPlace>

          <ItineraryFooter>
            {/* <div>Operated by: {departure.airline.name} ({departure.airline.iata})</div> */}
            <div>
              Operated by: {stops[0].airline ? stops[0].airline.name : departure.airline.name} (
              {stops[0].airline ? stops[0].airline.iata : departure.airline.iata})
            </div>
            <div>{ticketNumber && `Ticket number: ${ticketNumber}`}</div>
          </ItineraryFooter>
        </React.Fragment>
      )
    }
    case 'hotel': {
      const { hotel, checkIn, checkOut, cancellation, remarks } = data

      const { rooms } = hotel

      return (
        <React.Fragment>
          <ItineraryPlace>
            <div className='title'>{hotel.name}</div>
            <div className='address'>{hotel.address}</div>
            <div>
              <strong>{hotel.phone ? 'Tel:' : ''}</strong>
              <span className='ml-1'>
                <a href={`tel:+${hotel.phone.replace('+', '')}`}>{hotel.phone}</a>
              </span>
            </div>
            <div>
              <strong>Email:</strong>
              <span className='ml-1'>
                <a href={`mailto:${hotel.email}`}>{hotel.email}</a>
              </span>
            </div>
          </ItineraryPlace>

          <ItineraryPlace>
            <div className='booking-details'>
              <div className='checkin'>
                <strong>Check-in:</strong>
                <span className='ml-1'>{moment(checkIn).format('L')}</span>
              </div>
              <div className='checkout'>
                <strong>Check-out:</strong>
                <span className='ml-1'>{moment(checkOut).format('L')}</span>
              </div>
              <div className='cancellation-cost'>
                <strong>{cancellation ? `${I18n.t('dashboard_cancellation-cost')}:` : ''}</strong>
                <span className='ml-1'>
                  {cancellation && (
                    <React.Fragment>
                      <span className='price'>
                        {cancellation.price} {cancellation.priceCurrency}
                      </span>
                      <small>&nbsp;after {moment(cancellation.limitDate).format('L')}</small>
                    </React.Fragment>
                  )}
                </span>
              </div>
            </div>
          </ItineraryPlace>

          {/* 	<ItineraryPlace>
						<div className="remarks">
							{remarks && remarks.map((remark, i) =>
								<div key={i} className="remark">
									<strong>{remark.title ? remark.title + ':' : ''} </strong>
									<span>{remark.description}</span>
								</div>
							)}
						</div>
					</ItineraryPlace> */}

          <ItineraryFooter>
            {rooms.map((room, i) => (
              <div key={i} className='rate'>
                <div className='room-type'>{room.type}</div>
                <div className='price'>
                  <div>Total Price</div>
                  <div>
                    {room.price} {room.priceCurrency}
                  </div>
                </div>

                {!travelloopConnectors.includes(provider) && room.rateComments.length > 0 && (
                  <div className='rate-comments'>
                    {room.rateComments.map((rc, i) => (
                      <div key={i}>{rc}</div>
                    ))}
                  </div>
                )}

                {travelloopConnectors.includes(provider) && (
                  <div className='rate-comments'>
                    {remarks.map((rc, i) => (
                      <div key={i}>{rc}</div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </ItineraryFooter>
        </React.Fragment>
      )
    }
    default: {
      return null
    }
  }
}

function Index(props) {
  return <ItineraryDetails>{getItineraryFragment(props)}</ItineraryDetails>
}

Index.defaultProps = {
  data: {},
  type: '',
}

export default Index
